import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { AddOutlined, CloudSyncOutlined, RouteOutlined } from '@mui/icons-material';
import Header from '../../components/Header';
import { fetchData, postData } from '../../helpers/apiUtils';
import { CAuthor, CBroker, CConfig, CFeed, CItem, CSite } from './types';
import AuthorEditDialog from './AuthorEditDialog';
import BrokerConfigDialog from './BrokerConfigDialog';
import FeedCreateDialog from './FeedCreateDialog';

const HEAD_CELL = { backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' };

function parseArr(value: string | string[] | null): string[] {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const p = JSON.parse(value);
    return Array.isArray(p) ? p.map((v) => String(v)) : [];
  } catch {
    return [];
  }
}

/**
 * Broker Content Hub — centralized management of broker-delivered content feeds. Extends the
 * Broker Hub (profiles) with feeds + a canonical broker-author registry + per-broker config.
 * Dashboard is master; it re-emits one syn: feed per portal (pull). Auto-publish (no gate).
 */
const BrokerContentHub: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [feeds, setFeeds] = useState<CFeed[]>([]);
  const [authors, setAuthors] = useState<CAuthor[]>([]);
  const [sites, setSites] = useState<CSite[]>([]);
  const [brokers, setBrokers] = useState<CBroker[]>([]);
  const [items, setItems] = useState<CItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editAuthorId, setEditAuthorId] = useState<number | null>(null);
  const [configBrokerId, setConfigBrokerId] = useState<number | ''>('');
  const [configBroker, setConfigBroker] = useState<CBroker | null>(null);
  const [routingFeed, setRoutingFeed] = useState<CFeed | null>(null);
  const [createFeedOpen, setCreateFeedOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const [feedsRes, authorsRes, sitesRes, brokersRes, itemsRes] = await Promise.all([
      fetchData<CFeed[]>('/api/broker-content/feeds'),
      fetchData<CAuthor[]>('/api/broker-content/authors'),
      fetchData<CSite[]>('/api/broker-content/sites'),
      fetchData<CBroker[]>('/api/broker-content/brokers'),
      fetchData<CItem[]>('/api/broker-content/items?limit=100'),
    ]);
    if (feedsRes.success && feedsRes.data) setFeeds(feedsRes.data);
    else setError(feedsRes.error || 'Feeds konnten nicht geladen werden');
    if (authorsRes.success && authorsRes.data) setAuthors(authorsRes.data);
    if (sitesRes.success && sitesRes.data) setSites(sitesRes.data);
    if (brokersRes.success && brokersRes.data) setBrokers(brokersRes.data);
    if (itemsRes.success && itemsRes.data) setItems(itemsRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pollAll = async () => {
    setBusy(true);
    setMessage(null);
    const res = await postData<{ sourceName: string; created: number; updated: number }[]>(
      '/api/broker-content/poll',
      {},
    );
    setBusy(false);
    if (res.success && res.data) {
      const created = res.data.reduce((n, r) => n + (r.created || 0), 0);
      const updated = res.data.reduce((n, r) => n + (r.updated || 0), 0);
      setMessage(`Poll fertig: ${created} neu, ${updated} aktualisiert.`);
      load();
    } else {
      setError(res.error || 'Poll fehlgeschlagen');
    }
  };

  const pollOne = async (feed: CFeed) => {
    setBusy(true);
    const res = await postData(`/api/broker-content/feeds/${feed.id}/poll`, {});
    setBusy(false);
    if (res.success) {
      setMessage(`${feed.source_name} gepollt.`);
      load();
    } else setError(res.error || 'Poll fehlgeschlagen');
  };

  // Pause = stop ingesting (listActiveFeeds skips poll_status='paused'). Reversible; existing
  // items keep emitting. Prefer this over delete (items FK to feeds).
  const toggleFeed = async (feed: CFeed) => {
    const next = feed.poll_status === 'paused' ? 'active' : 'paused';
    setBusy(true);
    const res = await fetchData(`/api/broker-content/feeds/${feed.id}`, {
      method: 'PUT',
      body: JSON.stringify({ poll_status: next }),
    });
    setBusy(false);
    if (res.success) {
      setMessage(`${feed.source_name} ${next === 'paused' ? 'pausiert' : 'aktiviert'}.`);
      load();
    } else setError(res.error || 'Statuswechsel fehlgeschlagen');
  };

  const saveRouting = async (feed: CFeed, targetSites: string[]) => {
    const res = await fetchData(`/api/broker-content/feeds/${feed.id}`, {
      method: 'PUT',
      body: JSON.stringify({ target_sites: targetSites }),
    });
    if (res.success) {
      setRoutingFeed(null);
      load();
    } else setError(res.error || 'Speichern fehlgeschlagen');
  };

  // FT is the primary live consumer; link to its live pages. (base_url is per-portal, so this
  // becomes per-portal once other portals go live.)
  const ftBase = (
    sites.find((s) => s.site_key === 'ft')?.base_url || 'https://www.finanztrends.de'
  ).replace(/\/$/, '');
  const authorUrl = (slug: string) => `${ftBase}/autoren/${slug}/`;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="Broker Content Hub"
        subtitle="Zentrale Verwaltung der Broker-Content-Feeds, Autoren & Disclaimer"
      />

      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      {message && (
        <Typography color="success.main" sx={{ mb: 1 }}>
          {message}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<CloudSyncOutlined />}
          onClick={pollAll}
          disabled={busy}
        >
          Alle Feeds pollen
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddOutlined />}
          onClick={() => setCreateFeedOpen(true)}
        >
          Feed hinzufügen
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Feeds (${feeds.length})`} />
        <Tab label={`Autoren (${authors.length})`} />
        <Tab label="Broker-Konfig" />
        <Tab label={`Items (${items.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL}>Feed</TableCell>
                <TableCell sx={HEAD_CELL}>Broker</TableCell>
                <TableCell sx={HEAD_CELL}>Portale</TableCell>
                <TableCell sx={HEAD_CELL}>Status</TableCell>
                <TableCell sx={HEAD_CELL}>Zuletzt</TableCell>
                <TableCell sx={HEAD_CELL}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeds.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    {f.source_url ? (
                      <a href={f.source_url} target="_blank" rel="noreferrer">
                        {f.label} ↗
                      </a>
                    ) : (
                      f.label
                    )}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {f.source_name} · {f.format}
                    </Typography>
                  </TableCell>
                  <TableCell>{f.broker_name || f.broker_slug || f.broker_id}</TableCell>
                  <TableCell>
                    {(f.target_sites || []).map((s) => (
                      <Chip key={s} label={s} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={f.poll_status}
                      size="small"
                      color={
                        f.poll_status === 'error'
                          ? 'error'
                          : f.poll_status === 'paused'
                            ? 'default'
                            : 'success'
                      }
                    />
                    {f.last_error && (
                      <Typography variant="caption" display="block" color="error">
                        {f.last_error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{f.last_polled_at || '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => pollOne(f)}
                      disabled={busy || f.poll_status === 'paused'}
                    >
                      Pollen
                    </Button>
                    <Button
                      size="small"
                      startIcon={<RouteOutlined />}
                      onClick={() => setRoutingFeed(f)}
                    >
                      Routing
                    </Button>
                    <Button
                      size="small"
                      color={f.poll_status === 'paused' ? 'success' : 'warning'}
                      onClick={() => toggleFeed(f)}
                      disabled={busy}
                    >
                      {f.poll_status === 'paused' ? 'Aktivieren' : 'Pausieren'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL}>Name</TableCell>
                <TableCell sx={HEAD_CELL}>Slug</TableCell>
                <TableCell sx={HEAD_CELL}>Jobbezeichnung</TableCell>
                <TableCell sx={HEAD_CELL}>Foto</TableCell>
                <TableCell sx={HEAD_CELL}>sameAs</TableCell>
                <TableCell sx={HEAD_CELL}>Quelle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {authors.map((a) => (
                <TableRow
                  key={a.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setEditAuthorId(a.id)}
                >
                  <TableCell>{a.display_name}</TableCell>
                  <TableCell>
                    <a href={authorUrl(a.slug)} target="_blank" rel="noreferrer" onClick={stop}>
                      {a.slug} ↗
                    </a>
                  </TableCell>
                  <TableCell>{a.job_title || '—'}</TableCell>
                  <TableCell>{a.photo_url ? 'ja' : '—'}</TableCell>
                  <TableCell>{(a.sameas || []).length || '—'}</TableCell>
                  <TableCell>{a.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <Box sx={{ maxWidth: 480 }}>
          <Typography sx={{ mb: 1 }}>
            Broker auswählen, um Disclaimer & CTA zu bearbeiten:
          </Typography>
          <Select
            fullWidth
            size="small"
            value={configBrokerId}
            displayEmpty
            onChange={(e) => {
              const id = e.target.value === '' ? '' : Number(e.target.value);
              setConfigBrokerId(id);
              setConfigBroker(id === '' ? null : brokers.find((b) => b.id === id) || null);
            }}
          >
            <MenuItem value="">
              <em>Broker wählen…</em>
            </MenuItem>
            {brokers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {tab === 3 && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL}>Titel</TableCell>
                <TableCell sx={HEAD_CELL}>Broker</TableCell>
                <TableCell sx={HEAD_CELL}>Autor</TableCell>
                <TableCell sx={HEAD_CELL}>Datum</TableCell>
                <TableCell sx={HEAD_CELL}>Portale (Routing)</TableCell>
                <TableCell sx={HEAD_CELL}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id}>
                  <TableCell sx={{ maxWidth: 340 }}>
                    {i.published_url || i.link ? (
                      <a
                        href={(i.published_url || i.link) as string}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {i.title}
                      </a>
                    ) : (
                      i.title
                    )}
                    {i.published_url ? (
                      <Chip label="FT" size="small" color="success" sx={{ ml: 0.5, height: 18 }} />
                    ) : i.link ? (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (Quelle)
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{i.broker_name || i.broker_slug}</TableCell>
                  <TableCell>
                    {i.author_name ? (
                      i.author_slug ? (
                        <a href={authorUrl(i.author_slug)} target="_blank" rel="noreferrer">
                          {i.author_name}
                        </a>
                      ) : (
                        i.author_name
                      )
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{i.pubdate || '—'}</TableCell>
                  <TableCell>
                    {parseArr(i.target_sites).map((s) => (
                      <Chip key={s} label={s} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip label={i.status} size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AuthorEditDialog
        authorId={editAuthorId}
        onClose={() => setEditAuthorId(null)}
        onSaved={() => {
          setEditAuthorId(null);
          load();
        }}
      />

      <BrokerConfigDialog
        broker={configBroker}
        onClose={() => {
          setConfigBroker(null);
          setConfigBrokerId('');
        }}
        onSaved={() => {
          setConfigBroker(null);
          setConfigBrokerId('');
          setMessage('Broker-Konfiguration gespeichert.');
        }}
      />

      <RoutingDialog
        feed={routingFeed}
        sites={sites}
        onClose={() => setRoutingFeed(null)}
        onSave={saveRouting}
      />

      <FeedCreateDialog
        open={createFeedOpen}
        brokers={brokers}
        authors={authors}
        sites={sites}
        onClose={() => setCreateFeedOpen(false)}
        onSaved={() => {
          setCreateFeedOpen(false);
          setMessage('Feed angelegt.');
          load();
        }}
      />
    </Box>
  );
};

/** Small dialog: which portals a feed's items are routed to (target_sites). */
const RoutingDialog: React.FC<{
  feed: CFeed | null;
  sites: CSite[];
  onClose: () => void;
  onSave: (feed: CFeed, targetSites: string[]) => void;
}> = ({ feed, sites, onClose, onSave }) => {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(feed?.target_sites || []);
  }, [feed]);

  const toggle = (siteKey: string) =>
    setSelected((prev) =>
      prev.includes(siteKey) ? prev.filter((s) => s !== siteKey) : [...prev, siteKey],
    );

  return (
    <Dialog open={!!feed} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Routing{feed ? ` — ${feed.source_name}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Portale, die diesen Broker-Content erhalten:
        </Typography>
        {sites.map((s) => (
          <FormControlLabel
            key={s.site_key}
            control={
              <Checkbox
                checked={selected.includes(s.site_key)}
                onChange={() => toggle(s.site_key)}
              />
            }
            label={`${s.name} (${s.site_key})`}
            sx={{ display: 'block' }}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => feed && onSave(feed, selected)}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerContentHub;
